import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { AiVerificationPlanStrategy } from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { useSessions } from '../sessions-admin';
import { templateDefaultsToPatch } from './templateDefaults';
import {
  createAssignmentPlan,
  loadAssignmentPlan,
  saveAssignmentPlanPatch,
  type AssignmentPlanInput,
} from './useAssignmentPlans';
import { useVerificationTemplates } from './useVerificationTemplates';

/**
 * 配分の予約 作成 / 編集ページ (2026-09-04、REQ-4.3.20)。
 *   route: /districts/:districtId/verification/plans/new
 *          /districts/:districtId/verification/plans/:planId/edit
 *
 * イベント前に「配り方」だけを保存する。投稿・参加者はここでは選ばない (実行時にサーバが自動収集する)。
 * 実行は一覧ページ (VerificationAssignmentsPage) の「▶ 配分する」から。
 *
 * テンプレートを選ぶと、テンプレートの「配分デフォルト」を初期値として反映する (templateDefaults.ts)。
 * 用語: セッション / 対象セッション / 参加セッション。UI に内部キーは出さない。
 */

const STRATEGY_OPTIONS: { value: AiVerificationPlanStrategy; label: string; help: string }[] = [
  {
    value: 'random_per_user',
    label: 'ランダム配分',
    help: '各参加者に、対象の投稿の中から「1 人あたりの上限」までをランダムに割り当てます。',
  },
  {
    value: 'round_robin',
    label: '均等配分',
    help: '対象の投稿を参加者へ順番に 1 件ずつ割り当てます (1 件の投稿は 1 人だけが確認)。',
  },
  {
    value: 'all_users',
    label: '全員に全投稿',
    help: '対象の投稿すべてを全参加者に割り当てます (投稿数 × 参加者数が 10000 件以下の場合のみ)。',
  },
];

function parseIntOrNull(text: string): number | null {
  if (text.trim() === '') return null;
  const n = Number(text);
  return Number.isInteger(n) ? n : NaN;
}

export function AssignmentPlanEditPage() {
  const { districtId, planId } = useParams<{ districtId: string; planId?: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const templatesState = useVerificationTemplates(districtId ?? null);
  const sessionsState = useSessions(districtId ?? '');
  const { toast } = useToast();
  const navigate = useNavigate();

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;
  const actorUid = authState.user?.uid ?? '';

  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'notfound' | 'error'>(
    planId ? 'loading' : 'ready',
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [scopeMode, setScopeMode] = useState<'district' | 'perSession'>('perSession');
  const [targetSessionIds, setTargetSessionIds] = useState<string[]>([]);
  const [strategy, setStrategy] = useState<AiVerificationPlanStrategy>('random_per_user');
  const [excludeOwnPosts, setExcludeOwnPosts] = useState(false);
  const [maxPerUserText, setMaxPerUserText] = useState('10');
  const [minOwnPostsText, setMinOwnPostsText] = useState('0');
  const [preferOwnPosts, setPreferOwnPosts] = useState(false);
  const [sameSessionOnly, setSameSessionOnly] = useState(false);
  const [aiAnalyzedOnly, setAiAnalyzedOnly] = useState(true);
  const [includeUnlinkedPosts, setIncludeUnlinkedPosts] = useState(false);
  const [publishOnRun, setPublishOnRun] = useState(true);
  const [endAtOffsetDaysText, setEndAtOffsetDaysText] = useState('');
  const [templateNotes, setTemplateNotes] = useState<string[]>([]);

  // 既存予約の読込 (編集)
  useEffect(() => {
    if (!districtId || !planId) return;
    let cancelled = false;
    loadAssignmentPlan(districtId, planId)
      .then((p) => {
        if (cancelled) return;
        if (!p) {
          setLoadState('notfound');
          return;
        }
        setDisplayName(p.displayName);
        setDescription(p.description);
        setTemplateId(p.templateId);
        setScopeMode(p.scopeMode);
        setTargetSessionIds(p.targetSessionIds);
        setStrategy(p.strategy);
        setExcludeOwnPosts(p.constraints.excludeOwnPosts);
        setMaxPerUserText(
          p.constraints.maxPerUser !== undefined ? String(p.constraints.maxPerUser) : '',
        );
        setMinOwnPostsText(String(p.constraints.minOwnPosts ?? 0));
        setPreferOwnPosts(p.constraints.preferOwnPosts === true);
        setSameSessionOnly(p.constraints.sameSessionOnly === true);
        setAiAnalyzedOnly(p.candidateFilters.aiAnalyzedOnly);
        setIncludeUnlinkedPosts(p.candidateFilters.includeUnlinkedPosts);
        setPublishOnRun(p.publishOnRun);
        setEndAtOffsetDaysText(p.endAtOffsetDays !== null ? String(p.endAtOffsetDays) : '');
        setLoadState('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError((e as Error).message);
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, planId]);

  const templates = templatesState.status === 'ready' ? templatesState.items : [];
  const sessions = useMemo(
    () =>
      sessionsState.status === 'ready' ? sessionsState.items.filter((s) => !s.archivedAt) : [],
    [sessionsState],
  );

  /** テンプレート選択時: 配分デフォルトを初期値として反映 (docs/98 R-82)。 */
  function handleTemplateChange(nextId: string) {
    setTemplateId(nextId);
    const tpl = templates.find((t) => t.id === nextId);
    const patch = templateDefaultsToPatch(tpl);
    setTemplateNotes(patch.notes);
    if (!patch.hasAny) return;
    if (patch.strategy) setStrategy(patch.strategy);
    if (patch.maxPerUser !== undefined) setMaxPerUserText(String(patch.maxPerUser));
    if (patch.excludeOwnPosts !== undefined) setExcludeOwnPosts(patch.excludeOwnPosts);
    if (patch.minOwnPosts !== undefined) setMinOwnPostsText(String(patch.minOwnPosts));
    if (patch.preferOwnPosts !== undefined) setPreferOwnPosts(patch.preferOwnPosts);
    if (patch.sameSessionOnly !== undefined) setSameSessionOnly(patch.sameSessionOnly);
    if (patch.endAtOffsetDays !== undefined) setEndAtOffsetDaysText(String(patch.endAtOffsetDays));
    toast('info', 'テンプレートの推奨設定を反映しました（必要に応じて変更できます）');
  }

  const maxPerUser = parseIntOrNull(maxPerUserText);
  const minOwnPosts = parseIntOrNull(minOwnPostsText) ?? 0;
  const endAtOffsetDays = parseIntOrNull(endAtOffsetDaysText);
  const ownInclusionActive = strategy === 'random_per_user' && !excludeOwnPosts;

  const validationError = useMemo<string | null>(() => {
    if (!displayName.trim()) return '予約の名前を入力してください';
    if (!templateId) return 'テンプレートを選択してください';
    if (maxPerUser !== null && (Number.isNaN(maxPerUser) || maxPerUser <= 0)) {
      return '「1 人あたりの上限」は 1 以上の整数で入力してください（空欄 = 無制限）';
    }
    if (ownInclusionActive && (Number.isNaN(minOwnPosts) || minOwnPosts < 0)) {
      return '自分の投稿の最低件数は 0 以上の整数で入力してください';
    }
    if (ownInclusionActive && maxPerUser !== null && minOwnPosts > maxPerUser) {
      return '自分の投稿の最低件数は「1 人あたりの上限」以下にしてください';
    }
    if (endAtOffsetDays !== null && (Number.isNaN(endAtOffsetDays) || endAtOffsetDays < 0)) {
      return '回答期限の日数は 0 以上の整数で入力してください（空欄 = 期限なし）';
    }
    return null;
  }, [displayName, templateId, maxPerUser, ownInclusionActive, minOwnPosts, endAtOffsetDays]);

  function buildInput(): AssignmentPlanInput {
    return {
      displayName: displayName.trim(),
      description,
      templateId,
      scopeMode,
      targetSessionIds: scopeMode === 'perSession' ? targetSessionIds : [],
      strategy,
      constraints: {
        excludeOwnPosts,
        ...(maxPerUser !== null && !Number.isNaN(maxPerUser) ? { maxPerUser } : {}),
        ...(ownInclusionActive && minOwnPosts > 0 ? { minOwnPosts } : {}),
        ...(ownInclusionActive && preferOwnPosts ? { preferOwnPosts: true } : {}),
        // セッションごとの予約では参加セッション整合が常に有効なので、地区全体のときだけ保存する
        ...(scopeMode === 'district' && sameSessionOnly ? { sameSessionOnly: true } : {}),
      },
      candidateFilters: { aiAnalyzedOnly, includeUnlinkedPosts },
      publishOnRun,
      endAtOffsetDays:
        endAtOffsetDays !== null && !Number.isNaN(endAtOffsetDays) ? endAtOffsetDays : null,
    };
  }

  async function handleSave() {
    if (!districtId) return;
    if (validationError) {
      toast('error', validationError);
      return;
    }
    setSaving(true);
    try {
      const input = buildInput();
      if (planId) {
        await saveAssignmentPlanPatch(districtId, planId, input, actorUid);
        toast('success', '予約を保存しました');
      } else {
        await createAssignmentPlan(districtId, input, actorUid);
        toast(
          'success',
          '予約を作成しました。セッション終了後に一覧の「配分する」を押してください',
        );
      }
      navigate(`/districts/${districtId}/verification/assignmentSets`);
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const listPath = districtId
    ? `/districts/${districtId}/verification/assignmentSets`
    : '/districts';

  return (
    <>
      <header className="mb-6">
        <Link
          to={listPath}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 検証の配分
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          {planId ? '配分の予約を編集' : '配分の予約を作成'}
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          配り方だけを先に決めておき、セッションが終わったら一覧の「配分する」を押すだけで、その時点の投稿と参加者に配分します。
          もう一度押すと、その時点の投稿と参加者で配分しなおします。
        </p>
      </header>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">この地区の配分を管理する権限がありません。</p>
        </Card>
      )}
      {loadState === 'loading' && (
        <Card padding="md" className="mb-4">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}
      {loadState === 'notfound' && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">この予約は見つかりませんでした。</p>
        </Card>
      )}
      {loadState === 'error' && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">読込エラー: {loadError}</p>
        </Card>
      )}

      {loadState === 'ready' && (
        <>
          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">1. 名前</h2>
            <div className="grid grid-cols-1 gap-3">
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">
                  予約の名前 *（配分セットの名前にも使われます）
                </span>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={!canManage}
                  maxLength={120}
                  placeholder="例: 第 1 回 検証"
                  aria-label="予約の名前"
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm min-w-0"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">メモ（任意）</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!canManage}
                  rows={2}
                  aria-label="メモ"
                  className="px-3 py-2 border border-slate-300 rounded text-sm min-w-0"
                />
              </label>
            </div>
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">2. テンプレート</h2>
            {templatesState.status === 'ready' && templates.length === 0 && (
              <p className="text-sm text-warning-700">
                テンプレートがありません。先に
                <Link
                  to={districtId ? `/districts/${districtId}/verification/templates` : '/districts'}
                  className="mx-1 text-primary-700 underline"
                >
                  検証テンプレート
                </Link>
                を作成してください。
              </p>
            )}
            {templates.length > 0 && (
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">参加者に出す質問のテンプレート *</span>
                <select
                  value={templateId}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  disabled={!canManage}
                  aria-label="テンプレート"
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white min-w-0"
                  style={{ maxWidth: '100%' }}
                >
                  <option value="">— 選択してください —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.status === 'published' ? '' : '[下書き] '}
                      {t.displayName || '(無題)'}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {templateNotes
              // セッションごとの予約では対象セッションが自動で決まるため、「対象セッションを選んで」の促しは出さない
              .filter((n) => scopeMode === 'district' || !n.includes('対象セッション'))
              .map((n) => (
                <p key={n} className="mt-2 text-xs text-primary-800">
                  {n}
                </p>
              ))}
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">3. 対象</h2>
            <div className="grid grid-cols-1 gap-2">
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="scopeMode"
                  checked={scopeMode === 'perSession'}
                  onChange={() => setScopeMode('perSession')}
                  disabled={!canManage}
                  className="mt-1"
                  aria-label="セッションごとに配る"
                />
                <span>
                  <span className="font-medium">セッションごとに配る</span>
                  <span className="block text-[11px] text-slate-500">
                    セッションごとに 1
                    つずつ配分を作ります。各参加者には「自分の参加セッションの投稿」だけが配られます。
                    一覧では全セッション一括でも、セッション 1 つずつでも実行できます。
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="scopeMode"
                  checked={scopeMode === 'district'}
                  onChange={() => setScopeMode('district')}
                  disabled={!canManage}
                  className="mt-1"
                  aria-label="地区全体で 1 つ配る"
                />
                <span>
                  <span className="font-medium">地区全体で 1 つ配る</span>
                  <span className="block text-[11px] text-slate-500">
                    地区の全投稿を候補に、全参加者へ 1 つの配分を作ります。
                  </span>
                </span>
              </label>
            </div>

            {scopeMode === 'perSession' && (
              <div className="mt-3 border-t border-slate-200 pt-3">
                <p className="text-xs text-slate-500 mb-2">
                  対象にするセッション（何も選ばなければ、実行時点の全セッションが対象です。あとから追加したセッションも含まれます）
                </p>
                {sessions.length === 0 && (
                  <p className="text-sm text-slate-600">
                    この地区にはまだセッションが登録されていません。
                  </p>
                )}
                <div className="grid grid-cols-1 gap-1">
                  {sessions.map((s) => {
                    const checked = targetSessionIds.includes(s.id);
                    return (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setTargetSessionIds((prev) =>
                              prev.includes(s.id)
                                ? prev.filter((x) => x !== s.id)
                                : [...prev, s.id],
                            )
                          }
                          disabled={!canManage}
                          aria-label={`対象セッション ${s.name}`}
                        />
                        <span className="min-w-0">
                          {s.name}
                          <span className="ml-1 text-[11px] text-slate-500">
                            投稿 {s.postCount} 件
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  {targetSessionIds.length === 0
                    ? '未選択 — すべてのセッションが対象です。'
                    : `${targetSessionIds.length} 件のセッションを選択中。`}
                </p>
              </div>
            )}
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">4. 配り方</h2>
            <div className="grid grid-cols-1 gap-2 mb-3">
              {STRATEGY_OPTIONS.map((o) => (
                <label key={o.value} className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="strategy"
                    value={o.value}
                    checked={strategy === o.value}
                    onChange={() => setStrategy(o.value)}
                    disabled={!canManage}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">{o.label}</span>
                    <span className="block text-[11px] text-slate-500">{o.help}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-3">
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">
                  1 人あたりの上限（空欄 = 無制限、「全員に全投稿」では使いません）
                </span>
                <input
                  type="number"
                  value={maxPerUserText}
                  onChange={(e) => setMaxPerUserText(e.target.value)}
                  disabled={!canManage || strategy === 'all_users'}
                  min={1}
                  aria-label="1人あたりの上限"
                  className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={excludeOwnPosts}
                  onChange={(e) => setExcludeOwnPosts(e.target.checked)}
                  disabled={!canManage}
                  className="mt-1"
                  aria-label="自分の投稿を配分から除外する"
                />
                <span>
                  <span className="font-medium">自分の投稿を配分から除外する</span>
                  <span className="block text-[11px] text-slate-500">
                    各参加者には他の人の投稿だけを割り当てます。オフにすると、自分の投稿を含める設定ができます。
                  </span>
                </span>
              </label>
              {ownInclusionActive && (
                <div className="border border-primary-200 bg-primary-50/40 rounded p-3 grid grid-cols-1 gap-3">
                  <p className="text-xs text-slate-700">
                    <span className="font-medium text-primary-900">自分の投稿のセルフ検証</span>
                    <span className="block mt-0.5">
                      参加者本人に自分の投稿の AI
                      解析結果も確認してもらう設定です。端末を引き継いだ場合も、引き継ぎ前の投稿を自分の投稿として扱います。
                    </span>
                  </p>
                  <label className="grid grid-cols-1 gap-1">
                    <span className="text-xs text-slate-600">
                      自分の投稿を最低何件含めるか（0 = 指定なし）
                    </span>
                    <input
                      type="number"
                      value={minOwnPostsText}
                      onChange={(e) => setMinOwnPostsText(e.target.value)}
                      disabled={!canManage}
                      min={0}
                      step={1}
                      aria-label="自分の投稿を最低何件含めるか"
                      className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
                    />
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={preferOwnPosts}
                      onChange={(e) => setPreferOwnPosts(e.target.checked)}
                      disabled={!canManage}
                      className="mt-1"
                      aria-label="空き枠も自分の投稿で優先的に埋める"
                    />
                    <span>
                      <span className="font-medium">空き枠も自分の投稿で優先的に埋める</span>
                      <span className="block text-[11px] text-slate-500">
                        オフ:
                        自分の投稿は上の最低件数までにして、残りは他の人の投稿で埋めます（自分の投稿でいっぱいにしない）。
                      </span>
                    </span>
                  </label>
                </div>
              )}
              {scopeMode === 'district' && (
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={sameSessionOnly}
                    onChange={(e) => setSameSessionOnly(e.target.checked)}
                    disabled={!canManage}
                    className="mt-1"
                    aria-label="参加セッションの投稿だけを割り当てる"
                  />
                  <span>
                    <span className="font-medium">
                      各参加者には、その人の参加セッションの投稿だけを割り当てる
                    </span>
                    <span className="block text-[11px] text-slate-500">
                      地区全体で 1
                      つの配分にしつつ、割り当ては参加者ごとに参加セッションの投稿へ絞ります。
                    </span>
                  </span>
                </label>
              )}
              {strategy === 'all_users' && (
                <p className="text-xs text-warning-800 bg-warning-50 border border-warning-300 rounded p-2">
                  「全員に全投稿」は対象の投稿を全員に配ります。参加者数が多い場合は 1
                  人あたりの確認件数が非常に多くなります。
                </p>
              )}
            </div>
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">5. 候補にする投稿</h2>
            <div className="grid grid-cols-1 gap-2">
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={aiAnalyzedOnly}
                  onChange={(e) => setAiAnalyzedOnly(e.target.checked)}
                  disabled={!canManage}
                  className="mt-1"
                  aria-label="AI の解析が終わった投稿だけを配る"
                />
                <span>
                  <span className="font-medium">AI の解析が終わった投稿だけを配る</span>
                  <span className="block text-[11px] text-slate-500">
                    オフにすると解析待ちの投稿も配られますが、参加者には確認する内容が表示されません。
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={includeUnlinkedPosts}
                  onChange={(e) => setIncludeUnlinkedPosts(e.target.checked)}
                  disabled={!canManage}
                  className="mt-1"
                  aria-label="どのセッションにも紐づいていない投稿も含める"
                />
                <span>
                  <span className="font-medium">どのセッションにも紐づいていない投稿も含める</span>
                  <span className="block text-[11px] text-slate-500">
                    セッション運用を始める前の投稿などが対象になります。含めた場合、その投稿は全員に配られ得ます。
                  </span>
                </span>
              </label>
              <p className="text-[11px] text-slate-500">非公開の投稿は常に対象外です。</p>
            </div>
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-3">6. 実行したあと</h2>
            <div className="grid grid-cols-1 gap-3">
              <label className="flex items-start gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={publishOnRun}
                  onChange={(e) => setPublishOnRun(e.target.checked)}
                  disabled={!canManage}
                  className="mt-1"
                  aria-label="配分したらすぐ参加者に公開する"
                />
                <span>
                  <span className="font-medium">配分したらすぐ参加者に公開する</span>
                  <span className="block text-[11px] text-slate-500">
                    オフにすると下書きとして作られ、一覧で「公開中」にするまで参加者には表示されません。
                  </span>
                </span>
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">
                  回答期限（配分した日から何日後か。空欄 = 期限なし）
                </span>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={endAtOffsetDaysText}
                    onChange={(e) => setEndAtOffsetDaysText(e.target.value)}
                    disabled={!canManage}
                    min={0}
                    aria-label="回答期限の日数"
                    className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
                  />
                  <span className="text-xs text-slate-500">日後</span>
                </div>
              </label>
            </div>
          </Card>

          {validationError && (
            <Card padding="md" className="border-warning-600 mb-4">
              <p className="text-sm text-warning-700">⚠ {validationError}</p>
            </Card>
          )}

          <div className="flex items-center justify-end gap-2 mb-6 flex-wrap">
            <Link to={listPath} className="text-sm text-slate-600 hover:underline">
              キャンセル
            </Link>
            <Button
              onClick={() => void handleSave()}
              disabled={!canManage || !!validationError || saving}
            >
              {saving ? '保存中…' : planId ? '予約を保存' : '予約を作成'}
            </Button>
          </div>
        </>
      )}
    </>
  );
}
